import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ChefHat, Clock, Users, Search, Heart, HeartOff, Grid, BookmarkCheck, UsersRound } from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export const RecipesPage = () => {
  const { API, token } = useAuth();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [favorites, setFavorites] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDiet, setSelectedDiet] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  const categories = ['all', 'breakfast', 'lunch', 'dinner', 'snack'];
  const dietTypes = ['all', 'low-histamine', 'anti-inflammatory', 'MCAS-friendly', 'autoimmune', 'gut-healing'];

  useEffect(() => {
    fetchRecipes();
    fetchFavorites();
  }, []);

  useEffect(() => {
    filterRecipes();
  }, [recipes, selectedCategory, selectedDiet, searchQuery, favorites, activeTab]);

  const fetchRecipes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/recipes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipes(response.data);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/bookmarks?tool_type=recipe`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const favMap = {};
      response.data.forEach(b => {
        favMap[b.tool_id] = true;
      });
      setFavorites(favMap);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const toggleFavorite = async (e, recipe) => {
    e.stopPropagation();
    try {
      if (favorites[recipe.id]) {
        await axios.delete(`${API}/bookmarks/tool/recipe/${recipe.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(prev => ({ ...prev, [recipe.id]: false }));
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/bookmarks`, {
          tool_type: 'recipe',
          tool_id: recipe.id,
          tool_title: recipe.title,
          tool_category: recipe.category
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(prev => ({ ...prev, [recipe.id]: true }));
        toast.success('Added to favorites!');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const filterRecipes = () => {
    let filtered = recipes;

    // Filter by favorites tab
    if (activeTab === 'favorites') {
      filtered = filtered.filter(r => favorites[r.id]);
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(r => r.category === selectedCategory);
    }

    if (selectedDiet !== 'all') {
      filtered = filtered.filter(r => r.diet_type.includes(selectedDiet));
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.title.toLowerCase().includes(query) ||
        r.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    setFilteredRecipes(filtered);
  };

  const favoriteCount = Object.values(favorites).filter(Boolean).length;

  return (
    <div className="space-y-8" data-testid="recipes-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-secondary/20 to-primary/10 rounded-xl p-8 md:p-12">
        <div className="flex items-center gap-4 mb-4">
          <ChefHat className="w-12 h-12 text-primary" strokeWidth={1.5} />
          <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
            Autoimmune-Friendly Recipes
          </h1>
        </div>
        <p className="text-lg text-[#57534E] leading-relaxed">
          Delicious family meals and snacks designed for histamine intolerance, MCAS, and autoimmune support
        </p>
      </div>

      {/* Community Recipes Banner */}
      <Card className="bg-gradient-to-r from-secondary/20 to-primary/20 border-0 rounded-xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
              <UsersRound className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-nunito font-bold text-[#2D2D2D]">Community Recipes</h3>
              <p className="text-sm text-[#57534E]">Discover recipes shared by other parents or share your own!</p>
            </div>
          </div>
          <Button 
            onClick={() => navigate('/community-recipes')}
            className="bg-primary text-white hover:bg-primary/90 rounded-full"
            data-testid="community-recipes-btn"
          >
            Browse Community Recipes
          </Button>
        </div>
      </Card>

      {/* Tabs for All vs Favorites */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white rounded-xl border border-stone-200 p-1">
          <TabsTrigger 
            value="all" 
            className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white px-6"
          >
            <Grid className="w-4 h-4 mr-2" />
            All Recipes
          </TabsTrigger>
          <TabsTrigger 
            value="favorites" 
            className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white px-6"
          >
            <BookmarkCheck className="w-4 h-4 mr-2" />
            Favorites ({favoriteCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-6">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
            <Input
              type="text"
              placeholder="Search recipes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 bg-white border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-lg h-14 text-base"
              data-testid="recipe-search-input"
            />
          </div>

          {/* Filters */}
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-[#57534E] mb-3">Meal Type:</p>
              <div className="flex flex-wrap gap-3">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                      selectedCategory === cat
                        ? 'bg-primary text-white shadow-md'
                        : 'bg-white text-[#57534E] border border-stone-200 hover:border-primary'
                    }`}
                    data-testid={`category-${cat}`}
                  >
                    {cat === 'all' ? 'All Meals' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-[#57534E] mb-3">Diet Type:</p>
              <div className="flex flex-wrap gap-3">
                {dietTypes.map(diet => (
                  <button
                    key={diet}
                    onClick={() => setSelectedDiet(diet)}
                    className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                      selectedDiet === diet
                        ? 'bg-secondary text-secondary-foreground shadow-md'
                        : 'bg-white text-[#57534E] border border-stone-200 hover:border-secondary'
                    }`}
                    data-testid={`diet-${diet}`}
                  >
                    {diet === 'all' ? 'All Types' : diet.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="favorites" className="space-y-6">
          {favoriteCount === 0 && (
            <Card className="bg-white rounded-xl border border-stone-100 p-12 text-center">
              <Heart className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
              <p className="text-[#57534E] text-lg mb-2">No favorite recipes yet</p>
              <p className="text-[#A8A29E]">
                Click the heart icon on recipes you love to save them here
              </p>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Recipes Grid */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-[#A8A29E]">Loading recipes...</p>
        </div>
      ) : filteredRecipes.length === 0 && activeTab === 'all' ? (
        <Card className="bg-white rounded-xl border border-stone-100 p-12 text-center">
          <ChefHat className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
          <p className="text-[#57534E] text-lg">No recipes found matching your criteria</p>
        </Card>
      ) : filteredRecipes.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRecipes.map(recipe => (
            <Card
              key={recipe.id}
              className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6 hover:shadow-[0_8px_24px_rgba(124,154,146,0.15)] hover:border-primary/30 transition-all duration-300 cursor-pointer hover:-translate-y-1"
              onClick={() => navigate(`/recipes/${recipe.id}`)}
              data-testid={`recipe-card-${recipe.id}`}
            >
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <ChefHat className="w-8 h-8 text-primary flex-shrink-0" strokeWidth={1.5} />
                    <h3 className="text-xl font-nunito font-bold text-[#2D2D2D] leading-tight flex-1">
                      {recipe.title}
                    </h3>
                  </div>
                  <button
                    onClick={(e) => toggleFavorite(e, recipe)}
                    className={`p-2 rounded-full transition-colors ${
                      favorites[recipe.id] 
                        ? 'bg-red-50 text-red-500 hover:bg-red-100' 
                        : 'bg-stone-50 text-[#A8A29E] hover:text-red-500 hover:bg-red-50'
                    }`}
                    data-testid={`favorite-${recipe.id}`}
                  >
                    <Heart className={`w-5 h-5 ${favorites[recipe.id] ? 'fill-current' : ''}`} />
                  </button>
                </div>

                <div className="flex items-center gap-4 text-sm text-[#57534E]">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{recipe.prep_time + recipe.cook_time} min</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{recipe.servings} servings</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {recipe.diet_type.slice(0, 3).map(diet => (
                    <span
                      key={diet}
                      className="px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-xs font-medium"
                    >
                      {diet}
                    </span>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2">
                  {recipe.tags.slice(0, 3).map(tag => (
                    <span
                      key={tag}
                      className="px-2 py-1 bg-primary/10 text-primary rounded text-xs"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <Button
                  className="w-full bg-primary text-white hover:bg-primary/90 rounded-full font-semibold"
                  data-testid={`view-recipe-${recipe.id}`}
                >
                  View Recipe
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecipesPage;
